import React, { Component } from "react";
import { observer } from "mobx-react";
import { AxiosError, AxiosResponse } from "axios";
import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import { getRequest, putRequest } from "../../../../utils/httpRequest";
import { notification, randomNumber } from "../../../../utils/helpers";
import ProfileStore from "../../../../stores/ProfileStore";
import { Profile } from "../../../../stores/ProfileStore/Types";
import ProfilePictureUploader from "./ProfilePictureUploader";
import AccountInfo from "./AccountInfo";

interface Props {
  profileResource: Profile;
  profileStore: ProfileStore;
  getCurrentUser: () => void;
  profilePicture: string;
  firstName: string;
  lastName: string;
  locale: string;
  selectedCountry: string;
  userErrors: any;
  handleChange: (e: any, key: string) => void;
  handleCountryChange: (event: any, data: any) => void;
  handleLocaleChange: (event: any, data: any) => void;
}

interface State {
  pictureUrl: string;
  progress: number;
  uploadNewImage: boolean;
  uploadingImage: boolean;
  savingImage: boolean;
  countries: any;
  [x: string]: any;
  savingUser: boolean;
}

class Settings extends Component<Props, State> {
  private uppy: any;

  constructor(props: Props) {
    super(props);

    this.state = {
      pictureUrl: props.profileResource.picture_url,
      progress: 0,
      uploadNewImage: false,
      uploadingImage: false,
      savingImage: false,
      countries: [],
      savingUser: false,
    };

    this.uppy = Uppy({ autoProceed: true });
    this.configureUploader();
  }

  componentDidMount() {
    this.loadCountries();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.profileResource.picture_url !==
      this.props.profileResource.picture_url
    ) {
      this.setState({ pictureUrl: this.props.profileResource.picture_url });
    }

    if (prevProps.profileResource.id !== this.props.profileResource.id) {
      this.setState({ uploadNewImage: false });
    }
  }

  loadCountries = () => {
    return getRequest("/api/countries.json").then(
      (response: AxiosResponse<any>) => {
        const countries = response.data.map((countryResource) => {
          return {
            text: countryResource.country,
            value: countryResource.code,
            flag: countryResource.code.toLowerCase(),
            key: countryResource.code,
          };
        });

        this.setState({
          countries,
        });
      },
    );
  };

  componentWillUnmount() {
    this.uppy.close();
  }

  configureUploader = () => {
    this.uppy
      .use(Transloadit, {
        params: {
          auth: {
            key: process.env.TRANSLOADIT_AUTH_KEY,
            secret: process.env.TRANSLOADIT_AUTH_SECRET,
            duration: 1800,
          },
          templates: {
            s3_store: process.env.TRANSLOADIT_FILE_TEMPLATE_ID,
          },
          template_id: process.env.TRANSLOADIT_PROFILE_PIC_TEMPLATE_ID,
          steps: {
            resize: {
              robot: "/image/resize",
              width: 250,
              height: 250,
              resize_strategy: "fit",
              format: "webp",
            },
          },
        },
        waitForEncoding: true,
      })
      .on("progress", (progress: number) => {
        this.setState({ progress });
      })
      .on("error", this.handleUploadError)
      .on("upload-error", (_file, error, response) => {
        console.error("error message:", error);
        console.error("error response:", response);
        this.handleUploadError(error);
      })
      .on("transloadit:result", (_stepName, result) => {
        this.uploadFinished(result.url);
      });
  };

  handleUploadError = (error: any) => {
    this.setState({ uploadingImage: false, savingImage: false });
    notification("Error uploading image. Please try again", "error");
    console.error(error);

    if (error.assembly) {
      console.error(`Assembly ID ${error.assembly.assembly_id} failed!`);
      console.error(error.assembly);
    }

    (window as any).honeybadger?.notify(
      `Error uploading profile picture ${error}`,
    );
  };

  buildUploadDescriptor = (file: File) => ({
    id: `${randomNumber()}-${file.name}`,
    name: `${randomNumber()}${file.name}`,
    type: file.type,
    data: file,
  });

  addFile = (file: File) => {
    try {
      this.uppy.addFile(this.buildUploadDescriptor(file));
    } catch (error) {
      this.handleUploadError(error);
    }
  };

  getInitials = () => {
    const { first_name, last_name, name } = this.props.profileResource;
    const parts = [first_name, last_name].filter(Boolean);

    if (parts.length) {
      return parts
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    }

    const fallbackParts = name?.trim().split(/\s+/).filter(Boolean) || [];
    return fallbackParts
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  uploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    this.uppy.log("[ProfilePictureInput] Image selected through input.");
    this.setState({ uploadingImage: true, progress: 0 });
    this.addFile(file);
    event.target.value = "";
  };

  handleUploadNewImage = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    this.setState({ uploadNewImage: true });
  };

  uploadFinished = (url: string) => {
    this.setState({ pictureUrl: url, progress: 100, savingImage: true });
    this.saveProfilePicture(url);
  };

  saveProfilePicture = (pictureUrl: string) => {
    const { profileResource, profileStore, getCurrentUser } = this.props;

    return putRequest(`/api/users/${profileResource.id}.json`, {
      user: {
        first_name: profileResource.first_name,
        last_name: profileResource.last_name,
        email: profileResource.email,
        country: profileResource.country,
        locale: profileResource.locale,
        picture_url: pictureUrl,
        name_required: true,
        weekly_learning_digest_enabled:
          profileResource.weekly_learning_digest_enabled,
      },
    })
      .then((response: AxiosResponse) => {
        profileStore.setProfileResource(response.data);
        getCurrentUser();
        this.setState({
          uploadingImage: false,
          savingImage: false,
          uploadNewImage: false,
        });
        notification(
          "Profile picture was successfully updated.",
          "notification",
        );
      })
      .catch((error: AxiosError) => {
        this.setState({ uploadingImage: false, savingImage: false });
        notification("Unable to save profile picture.", "error");
        console.error(error.response?.statusText || error);
      });
  };

  render() {
    const { profilePicture, profileResource } = this.props;
    const {
      firstName,
      lastName,
      locale,
      selectedCountry,
      userErrors,
      handleChange,
      handleCountryChange,
      handleLocaleChange,
    } = this.props;
    const {
      pictureUrl,
      progress,
      savingImage,
      uploadNewImage,
      uploadingImage,
      countries,
    } = this.state;

    return (
      <>
        <ProfilePictureUploader
          uploadNewImage={uploadNewImage}
          pictureUrl={pictureUrl}
          profilePicture={profilePicture}
          initials={this.getInitials()}
          uploadingImage={uploadingImage || savingImage}
          progress={progress}
          uploadFile={this.uploadFile}
          onChangeImage={this.handleUploadNewImage}
        />
        <AccountInfo
          firstName={firstName}
          lastName={lastName}
          locale={locale}
          memberSince={profileResource.date_joined}
          handleChange={handleChange}
          countries={countries}
          selectedCountry={selectedCountry}
          handleCountryChange={handleCountryChange}
          handleLocaleChange={handleLocaleChange}
          userErrors={userErrors}
        />
      </>
    );
  }
}

export default observer(Settings);
